import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@services/firebase';
import toast from 'react-hot-toast';

/** Fetch role from Firestore users/{uid} doc */
const fetchUserRole = async (uid) => {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? (snap.data().role ?? 'free_user') : 'free_user';
  } catch {
    return 'free_user';
  }
};

/** Create or merge a users/{uid} doc on sign-in */
const upsertUserDoc = async (firebaseUser) => {
  const ref = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || '',
      role: 'free_user',
      createdAt: serverTimestamp(),
    });
    return 'free_user';
  }
  // Update mutable fields but never overwrite role
  await setDoc(ref, {
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || snap.data().displayName || '',
    photoURL: firebaseUser.photoURL || snap.data().photoURL || '',
    lastLoginAt: serverTimestamp(),
  }, { merge: true });
  return snap.data().role ?? 'free_user';
};

/** Map a Firebase user object + role to a plain serialisable shape */
const mapUser = (firebaseUser, role = 'free_user') => ({
  uid:          firebaseUser.uid,
  email:        firebaseUser.email,
  displayName:  firebaseUser.displayName,
  photoURL:     firebaseUser.photoURL,
  emailVerified: firebaseUser.emailVerified,
  role,
});

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      loading: true,
      error: null,

      // Initialize Firebase auth state listener — call once on app mount
      initialize: () => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            const role = await fetchUserRole(firebaseUser.uid);
            set({ user: mapUser(firebaseUser, role), loading: false, error: null });
          } else {
            set({ user: null, loading: false });
          }
        });
        return unsubscribe;
      },

      // Login with email and password (pure Firebase)
      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);
          const role = await upsertUserDoc(firebaseUser);
          const user = mapUser(firebaseUser, role);
          set({ user, loading: false, error: null });
          toast.success('Successfully logged in!');
          return user;
        } catch (error) {
          const errorMessage = friendlyAuthError(error);
          set({ loading: false, error: errorMessage });
          toast.error(errorMessage);
          throw error;
        }
      },

      // Register new user (pure Firebase)
      register: async (email, password, displayName) => {
        set({ loading: true, error: null });
        try {
          const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(firebaseUser, { displayName });
          const role = await upsertUserDoc({ ...firebaseUser, displayName });
          const user = mapUser({ ...firebaseUser, displayName }, role);
          set({ user, loading: false, error: null });
          toast.success('Account created successfully!');
          return user;
        } catch (error) {
          const errorMessage = friendlyAuthError(error);
          set({ loading: false, error: errorMessage });
          toast.error(errorMessage);
          throw error;
        }
      },

      // Login / register with Google popup (pure Firebase)
      loginWithGoogle: async () => {
        set({ loading: true, error: null });
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          const { user: firebaseUser } = await signInWithPopup(auth, provider);
          const role = await upsertUserDoc(firebaseUser);
          const user = mapUser(firebaseUser, role);
          set({ user, loading: false, error: null });
          toast.success('Signed in with Google!');
          return user;
        } catch (error) {
          if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
            set({ loading: false });
            return;
          }
          const errorMessage = friendlyAuthError(error);
          set({ loading: false, error: errorMessage });
          toast.error(errorMessage);
          throw error;
        }
      },

      // Logout
      logout: async () => {
        set({ loading: true, error: null });
        try {
          await firebaseSignOut(auth);
          set({ user: null, loading: false, error: null });
          toast.success('Successfully logged out');
        } catch (error) {
          const errorMessage = friendlyAuthError(error);
          set({ loading: false, error: errorMessage });
          toast.error(errorMessage);
          throw error;
        }
      },

      // Send password-reset email
      forgotPassword: async (email) => {
        set({ loading: true, error: null });
        try {
          await sendPasswordResetEmail(auth, email);
          set({ loading: false, error: null });
          toast.success('Password reset email sent!');
        } catch (error) {
          const errorMessage = friendlyAuthError(error);
          set({ loading: false, error: errorMessage });
          toast.error(errorMessage);
          throw error;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
    }
  )
);

/** Convert Firebase Auth error codes to human-friendly messages */
function friendlyAuthError(error) {
  const messages = {
    'auth/user-not-found':       'No account found with this email.',
    'auth/wrong-password':       'Incorrect password.',
    'auth/invalid-credential':   'Incorrect email or password.',
    'auth/email-already-in-use': 'An account already exists with this email.',
    'auth/weak-password':        'Password must be at least 6 characters.',
    'auth/invalid-email':        'Please enter a valid email address.',
    'auth/too-many-requests':    'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/user-disabled':        'This account has been disabled.',
  };
  return messages[error.code] || error.message || 'Authentication failed.';
}

export default useAuthStore;
